import type { FC, Ref, RefObject } from 'react';
import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { getDocumentationUrl } from '@gonfalon/docs/documentationUrl';
import {
  enableObservabilityAiSuggestedPrompts,
  enableVegaDraftWhileAnalyzing,
  enableVegaPageContext,
} from '@gonfalon/dogfood-flags/dogfood-flags';
import { ConnectGitHubButton } from '@gonfalon/github-integration/components/ConnectGitHubButton';
import { GitHubRepoSelector } from '@gonfalon/github-integration/components/GitHubRepoSelector';
import { useGitHubIntegration } from '@gonfalon/github-integration/hooks/useGitHubIntegration';
import { useVegaRepoSelection } from '@gonfalon/github-integration/hooks/useVegaRepoSelection';
import { Message } from '@gonfalon/messages/Message';
import { msg } from '@gonfalon/messages/msg';
import { toDiagnosticUsage, toHref, toVegaSettings } from '@gonfalon/navigator/navigators';
import { Box } from '@gonfalon/observability-ui/components/Box';
import { IconSolidGithub } from '@gonfalon/observability-ui/components/icons';
import { Stack } from '@gonfalon/observability-ui/components/Stack';
import { Text } from '@gonfalon/observability-ui/components/Text';
import { AI_BOT_NAME } from '@gonfalon/observability-ui/constants';
import { useProjectId } from '@gonfalon/observability-ui/hooks/useProjectId';
import { Alert, AlertText, Button, ButtonGroup, IconButton, Text as LPText } from '@launchpad-ui/components';
import type { IconName } from '@launchpad-ui/icons';
import { Icon } from '@launchpad-ui/icons';

import { useAgentInsights } from '../../AgentInsightsContext';
import type { ExternalSourceType } from '../../AgentInsightsContext';
import { buildPrompt, mergeAndDedupeResources } from '../../agentInsightsUtils';
import { PermissionModePicker } from '../../internal/components/AgentInsights/PermissionModePicker';
import { SendStopButton } from '../../internal/components/AgentInsights/SendStopButton';
import { useRotatingPrompts } from '../../internal/components/AgentInsights/useRotatingPrompts';
import { getVisualTestRand } from '../../internal/components/AgentInsights/visualTestRand';
import { usePageContext } from '../../internal/hooks/usePageContext';
import { isExternalSourceType } from '../../resources';
import type { ResourceOrQuery } from '../../resources';
import { trackVegaAiModeChanged, trackVegaAiPromptSent, trackVegaAiSuggestedPromptClicked } from '../../vegaAnalytics';
import type { VegaMentionsInputHandle } from '../mentions';
import { VegaMentionsInput } from '../mentions';

import * as styles from '../../internal/components/AgentInsights/AgentInsights.css';

const DRAFT_PROMPT_STORAGE_KEY = 'vega-draft-prompt';

function saveDraftToStorage(text: string) {
  try {
    if (text) {
      localStorage.setItem(DRAFT_PROMPT_STORAGE_KEY, text);
    } else {
      localStorage.removeItem(DRAFT_PROMPT_STORAGE_KEY);
    }
  } catch {
    // ignore storage errors
  }
}

function loadDraftFromStorage(): string {
  try {
    return localStorage.getItem(DRAFT_PROMPT_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
}

function clearDraftFromStorage() {
  try {
    localStorage.removeItem(DRAFT_PROMPT_STORAGE_KEY);
  } catch {
    // ignore storage errors
  }
}

/**
 * Converts a plain-text draft (with `\n` line breaks) to Tiptap-compatible
 * HTML so that multi-line content is faithfully restored. Each line becomes a
 * `<p>` element; an empty line becomes an empty `<p>`, which ProseMirror keeps
 * as an empty paragraph. (A `<br>` would parse as a hardBreak node and read back
 * as an extra newline, growing the draft on every restore.)
 * Special HTML characters in the text are escaped to avoid parse issues.
 */
export function draftToTiptapHtml(text: string): string {
  if (!text) {
    return '';
  }
  return text
    .split('\n')
    .map((line) => {
      const escaped = line.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
      return `<p>${escaped}</p>`;
    })
    .join('');
}

function UsageLimitBanner({
  currentCount,
  limit,
  onDismiss,
}: {
  currentCount: number;
  limit: number;
  onDismiss: () => void;
}) {
  return (
    <Alert status="warning" actionsLayout="inline">
      <AlertText>
        <LPText size="small">
          <Message id="vega.composer.usageLimitMessage" values={{ count: currentCount, limit }} />
        </LPText>
      </AlertText>
      {/* Own buttons rather than Alert's `isDismissable`, so each action keeps a label that names it. */}
      <ButtonGroup spacing="compact">
        <IconButton
          icon="link-external"
          variant="minimal"
          size="small"
          aria-label={msg('vega.composer.usageLimitContactLabel')}
          onPress={() => {
            window.open(toDiagnosticUsage().pathname, '_blank');
          }}
        />
        <IconButton
          icon="cancel"
          variant="minimal"
          size="small"
          aria-label={msg('vega.composer.usageLimitDismissLabel')}
          onPress={onDismiss}
        />
      </ButtonGroup>
    </Alert>
  );
}

function externalSourceLabel({ url, sourceType }: { url: string; sourceType: ExternalSourceType }): string {
  if (sourceType === 'github') {
    try {
      const { pathname } = new URL(url);
      if (/^\/[^/]+\/[^/]+\/pull\//.test(pathname)) {
        return msg('vega.composer.sourcePullRequest');
      }
      if (/^\/[^/]+\/[^/]+\/issues\//.test(pathname)) {
        return msg('vega.composer.sourceIssue');
      }
    } catch {
      // fall through to generic label
    }
    return msg('vega.composer.sourceGithub');
  }
  switch (sourceType) {
    case 'slack':
      return msg('vega.composer.sourceSlack');
    case 'confluence':
      return msg('vega.composer.sourceConfluence');
    case 'jira':
      return msg('vega.composer.sourceJira');
    case 'atlas':
      return msg('vega.composer.sourceAtlas');
    default:
      return msg('vega.composer.sourceGeneric');
  }
}

export interface PromptSubmission {
  /** The raw text the user typed. */
  rawPrompt: string;
  /** The enhanced prompt with context baked in. */
  prompt: string;
  repositories: string[];
  resources: ResourceOrQuery[];
}

/**
 * Imperative surface for hosts that open a conversation with the prompt already decided and send it
 * without the member pressing send.
 */
export interface VegaPromptComposerHandle {
  /**
   * Submits the prompt through the same guarded path as the send button, so it is a no-op while the
   * send mutation is in flight, the account is at its usage limit, or the agent is still responding.
   */
  handleSendPrompt: (prompt: string) => void;
  /**
   * Whether a submission would carry the right repositories yet — false until the GitHub
   * integration query resolves and any `preferredRepositories` are the selection. Sending earlier
   * files the run with `repositories: []`, or with whatever another Vega surface left in the shared
   * selection. Reading it off the ref doesn't re-render the host, so the host needs its own trigger
   * to re-check.
   */
  isReady: boolean;
}

export interface VegaPromptComposerProps {
  /** Called when the user submits a prompt. The consumer decides what to do with it. */
  onSubmit: (submission: PromptSubmission) => void;
  aiChatLimitData?: {
    aiChatLimitStatus: {
      current_count: number;
      limit: number;
    } | null;
  };
  /** Whether the send button is disabled (e.g. while the mutation is in flight). */
  disabled?: boolean;
  /** Whether the agent is generating a response. Shows the stop button; when
   *  `enable-vega-draft-while-analyzing` is on, the text input and toolbar stay
   *  usable so the user can draft a follow-up (mode, repos, settings) while waiting. */
  isAnalyzing?: boolean;
  /** Called when the user clicks the stop button during analysis. */
  onStopGeneration?: () => void;
  /** Show the "conversation was stopped early" warning. */
  showIncompleteWarning?: boolean;
  /** Whether to show suggested prompts (when there's no conversation history). */
  showSuggestedPrompts?: boolean;
  /** Whether to clear the textarea after submitting. Defaults to true.
   *  Set to false when the consumer navigates away (the unmount handles cleanup). */
  clearOnSubmit?: boolean;
  /** Conversation ID for analytics tracking (provided by AgentInsights for follow-up prompts). */
  conversationId?: string;
  /** Ref that receives a focus callback so external code can focus the textarea. */
  onFocusRef?: RefObject<(() => void) | null>;
  /** Ref that receives a clear callback so external code can clear the draft (e.g. New chat). */
  onClearPromptRef?: RefObject<(() => void) | null>;
  /** When true, render only the send/stop button in the footer — no settings gear, mode picker, or repo
   *  selector. Used by account-scoped skill panels (e.g. pricing concierge) that don't operate on code. */
  simplifiedFooter?: boolean;
  persistDraft?: boolean;
  /** The agent response that has been selected by the user (eg: checkbox selections). */
  agentResponse?: string;
  /** See VegaPromptComposerHandle. */
  ref?: Ref<VegaPromptComposerHandle>;
  contextItems?: ResourceOrQuery[];
  onContextItemsInserted?: () => void;
}

export const VegaPromptComposer: FC<VegaPromptComposerProps> = ({
  onSubmit,
  aiChatLimitData,
  disabled = false,
  isAnalyzing = false,
  onStopGeneration,
  showIncompleteWarning = false,
  showSuggestedPrompts: showSuggestedPromptsProp = true,
  clearOnSubmit = true,
  conversationId,
  onFocusRef,
  onClearPromptRef,
  agentResponse = '',
  simplifiedFooter = false,
  persistDraft = true,
  ref,
  contextItems: ownContextItems,
  onContextItemsInserted,
}) => {
  const enableSuggestedPrompts = enableObservabilityAiSuggestedPrompts();
  // When off, the composer stays locked while the agent responds (the
  // behavior before this flag) and there is nothing to draft or submit.
  const canDraftWhileAnalyzing = enableVegaDraftWhileAnalyzing();
  const pageContextEnabled = enableVegaPageContext();
  // Mode, repos, and settings are read on the *next* send, so they can stay
  // available while a turn is streaming. Still lock everything while the send
  // mutation is in flight (`disabled`).
  const composerChromeDisabled = canDraftWhileAnalyzing ? disabled : disabled || isAnalyzing;

  const {
    initialQuery,
    context: agentContext,
    clearContext,
    resource,
    preferredRepositories,
    retrievedConversationData,
    conversationClearSignal,
    permissionMode,
    setPermissionMode,
  } = useAgentInsights();

  const vegaMentionsRef = useRef<VegaMentionsInputHandle | null>(null);

  const { projectId, projectKey } = useProjectId();

  // Hide the default initial query when suggested prompts are shown,
  // but never hide an explicit initialQuery (e.g. from flag cleanup).
  const hideInitialQuery = enableSuggestedPrompts && showSuggestedPromptsProp && !initialQuery;
  const initialDraft = useMemo(() => (persistDraft ? loadDraftFromStorage() : ''), [persistDraft]);
  const [prompt, setPrompt] = useState(initialQuery || initialDraft);
  // What the user is looking at behind the panel. Read here, snapshotted into
  // the prompt at submit — see the submit handler.
  const derivedPageContext = usePageContext();
  const pageContext = pageContextEnabled ? derivedPageContext : undefined;
  // ExternalSource items are migrated into the editor as inline mention chips and
  // the context is cleared (see effect below). Capture the source url + sourceType
  // here so the placeholder can stay contextual even after the context is empty.
  const [externalSourceHint, setExternalSourceHint] = useState<{
    url: string;
    sourceType: ExternalSourceType;
  } | null>(null);

  const [isUsageBannerDismissed, setIsUsageBannerDismissed] = useState(false);

  const githubIntegration = useGitHubIntegration(projectId);
  const { privateRepos, isReady: githubIntegrated, isLoading: isLoadingVegaIntegrationStatus } = githubIntegration;
  const repoSelection = useVegaRepoSelection(githubIntegration);
  const { setSelected: setSelectedRepos, selectedKeys: selectedRepoKeys } = repoSelection;

  // The keys `preferredRepositories` resolves to. Matched against the private list because a public
  // repo can't be selected — its key gets clamped straight back out of the selection.
  const preferredRepoKeys = useMemo(() => {
    if (preferredRepositories.length === 0) {
      return [];
    }
    const preferredLower = preferredRepositories.map((r) => r.toLowerCase());
    return privateRepos
      .filter((repo) => {
        const repoNameLower = repo.name.toLowerCase();
        return preferredLower.some((pref) => repoNameLower === pref || repoNameLower.endsWith(`/${pref}`));
      })
      .map((repo) => repo.key);
  }, [preferredRepositories, privateRepos]);

  // Exact match, not "contains": the selection is a shared local-storage slot, so it can already
  // hold repos from another surface, and those would ride along on the submission.
  const arePreferredReposApplied =
    preferredRepoKeys.length === selectedRepoKeys.length &&
    preferredRepoKeys.every((key) => selectedRepoKeys.includes(key));

  // `preferredRepositories` seeds the selection, once per set of keys it resolves to. Enforcing it
  // on every render instead would make the picker unusable: a manual change leaves the selection
  // different from the preferred keys again, and the effect would write them straight back.
  const seededPreferredRepoKeysRef = useRef<string | null>(null);
  const preferredRepoKeysId = preferredRepoKeys.join(',');
  useEffect(() => {
    if (preferredRepoKeys.length === 0) {
      // Nothing to seed, and nothing seeded worth remembering: a host that asks for the same repos
      // again later has to be able to re-seed them, or it would send against a stale selection.
      seededPreferredRepoKeysRef.current = null;
      return;
    }
    if (seededPreferredRepoKeysRef.current === preferredRepoKeysId) {
      return;
    }
    seededPreferredRepoKeysRef.current = preferredRepoKeysId;
    // Already the selection — a sticky `all` that happens to resolve to exactly these repos. Leave
    // it, so writing explicit keys doesn't cost it its stickiness.
    if (arePreferredReposApplied) {
      return;
    }
    setSelectedRepos(preferredRepoKeys);
  }, [preferredRepoKeys, preferredRepoKeysId, arePreferredReposApplied, setSelectedRepos]);

  // The query the caller wants pre-filled, or '' when there is none to show.
  const seededQuery = initialQuery && !hideInitialQuery ? initialQuery : '';

  // Single path for writing the editor, so seeding and clearing can't disagree
  // about what the editor holds.
  const writePrompt = useCallback((text: string) => {
    setPrompt(text);
    if (text) {
      vegaMentionsRef.current?.setContent(draftToTiptapHtml(text));
    } else {
      vegaMentionsRef.current?.clear();
    }
  }, []);

  // Imperatively sync initialQuery into the Tiptap editor because initialContent is only read on mount.
  const [appliedSeed, setAppliedSeed] = useState('');
  if (seededQuery && seededQuery !== appliedSeed) {
    setAppliedSeed(seededQuery);
    setPrompt(seededQuery);
  }
  useEffect(() => {
    if (!seededQuery) {
      return;
    }
    vegaMentionsRef.current?.setContent(draftToTiptapHtml(seededQuery));
  }, [seededQuery]);

  // Resources attached via contextual entry points ("Ask Vega" on a log/span/etc.) are
  // migrated into the editor as inline mention chips. Historical messages are rendered
  // by UserMessage, which keeps its own ContextPills path for pre-V2 stored conversations.
  const ownsContext = ownContextItems !== undefined;
  const contextItems = ownContextItems ?? agentContext?.resourceOrQuery ?? [];
  const externalSource = contextItems.find((item) => item.type === 'ExternalSource');
  if (
    externalSource &&
    externalSource.payload &&
    typeof externalSource.payload.url === 'string' &&
    isExternalSourceType(externalSource.payload.sourceType) &&
    (externalSourceHint?.url !== externalSource.payload.url ||
      externalSourceHint?.sourceType !== externalSource.payload.sourceType)
  ) {
    setExternalSourceHint({
      url: externalSource.payload.url,
      sourceType: externalSource.payload.sourceType,
    });
  }
  useEffect(() => {
    if (contextItems.length === 0 || !vegaMentionsRef.current) {
      return;
    }
    contextItems.forEach((item) => vegaMentionsRef.current?.insertMention(item));
    if (ownsContext) {
      onContextItemsInserted?.();
    } else {
      clearContext();
    }
  }, [contextItems, clearContext, ownsContext, onContextItemsInserted]);

  const handleFocus = useCallback(() => {
    vegaMentionsRef.current?.focus();
  }, []);

  useEffect(() => {
    if (onFocusRef) {
      onFocusRef.current = handleFocus;
    }
  }, [onFocusRef, handleFocus]);

  // Persist the in-progress draft to localStorage so it survives navigation/refresh
  useEffect(() => {
    if (persistDraft) {
      saveDraftToStorage(prompt);
    }
  }, [prompt, persistDraft]);

  // Clear the draft and prompt when a historical conversation is loaded. Drop the
  // contextual hint too — it belonged to whatever conversation we were composing
  // before the load, and the new conversation hasn't introduced its own source yet.
  const [seenRetrievedConversation, setSeenRetrievedConversation] = useState<
    typeof retrievedConversationData | undefined
  >(undefined);
  if (retrievedConversationData !== seenRetrievedConversation) {
    setSeenRetrievedConversation(retrievedConversationData);
    if (retrievedConversationData) {
      setPrompt('');
      setExternalSourceHint(null);
    }
  }
  useEffect(() => {
    if (!retrievedConversationData) {
      return;
    }
    vegaMentionsRef.current?.clear();
    if (persistDraft) {
      clearDraftFromStorage();
    }
  }, [retrievedConversationData, persistDraft]);

  // Clear the draft and prompt when startConversationWithSource triggers a new
  // conversation. The hint is deliberately NOT reset here: startConversationWithSource
  // always attaches a new ExternalSource to agentContext, which the resource
  // migration effect picks up to set the new hint. Resetting here would race the
  // migration write (React 18 batches both state updates in this render cycle and
  // the last write wins — see the bug history at O11Y-1492 review).
  //
  // Entry points that seed a prompt while starting a new conversation (e.g. "Create with
  // Vega") set `initialQuery` and bump this signal in the same update, so this effect runs
  // in the same commit as the seeding effect above and would otherwise wipe the seed.
  // Writing `seededQuery` rather than '' keeps that prompt, and still clears when the new
  // conversation has nothing to pre-fill.
  const lastSeenClearSignal = useRef(conversationClearSignal);
  useEffect(() => {
    if (conversationClearSignal !== lastSeenClearSignal.current) {
      lastSeenClearSignal.current = conversationClearSignal;
      writePrompt(seededQuery);
      if (persistDraft) {
        clearDraftFromStorage();
      }
    }
  }, [conversationClearSignal, seededQuery, writePrompt, persistDraft]);

  // Expose a clear callback so AgentInsights can clear the draft on "New chat".
  // Resetting the hint here is the path that handles "user opened a PR-seeded
  // conversation, then clicked New chat to start a generic one" — otherwise the
  // placeholder would keep saying "Ask Vega about this PR…" with no source attached.
  const clearPrompt = useCallback(() => {
    setPrompt('');
    vegaMentionsRef.current?.clear();
    if (persistDraft) {
      clearDraftFromStorage();
    }
    setExternalSourceHint(null);
  }, [persistDraft]);

  useEffect(() => {
    if (onClearPromptRef) {
      onClearPromptRef.current = clearPrompt;
    }
  }, [onClearPromptRef, clearPrompt]);

  const { visiblePrompts, exitingKey } = useRotatingPrompts(
    resource,
    enableSuggestedPrompts && showSuggestedPromptsProp,
    getVisualTestRand(),
  );

  const navigate = useNavigate();
  const handleOpenSettings = useCallback(() => {
    navigate(toHref(toVegaSettings({ projectKey })));
  }, [navigate, projectKey]);
  const isAtUsageLimit =
    !!aiChatLimitData?.aiChatLimitStatus &&
    aiChatLimitData.aiChatLimitStatus.limit > 0 &&
    aiChatLimitData.aiChatLimitStatus.current_count >= aiChatLimitData.aiChatLimitStatus.limit;

  const handleSendPrompt = useCallback(
    (promptText?: string, isSuggestedPrompt = false) => {
      // Every gate the send button enforces lives here, not just on the button: the keyboard
      // shortcut, suggested prompts, and the imperative handle all come through this function, and
      // a host that auto-sends must not be able to start a run the UI would have refused.
      // Drafting while the agent responds is allowed; submitting is not.
      if (disabled || isAtUsageLimit || isAnalyzing) {
        return;
      }

      let actualPrompt = promptText || prompt;
      if (agentResponse) {
        actualPrompt = `User selection: ${agentResponse}\n\n${actualPrompt}`;
      }
      if (!actualPrompt.trim()) {
        return;
      }

      // Collect mention resources before clearing the editor
      const mentionResources = vegaMentionsRef.current?.getMentionResources() ?? [];

      if (clearOnSubmit) {
        setPrompt('');
        vegaMentionsRef.current?.clear();
      }

      const allResources = mergeAndDedupeResources(
        ownsContext ? [] : (agentContext?.resourceOrQuery ?? []),
        mentionResources,
      );
      const enhancedPrompt = buildPrompt(actualPrompt, allResources, pageContext);

      trackVegaAiPromptSent(permissionMode, {
        hasContext: allResources.length > 0,
        contextCount: allResources.length,
        hasPageContext: !!pageContext,
        pageContextView: pageContext?.view,
        isSuggestedPrompt,
        conversationId: conversationId || undefined,
      });

      onSubmit({
        rawPrompt: actualPrompt,
        prompt: enhancedPrompt,
        repositories: repoSelection.selectedNames,
        resources: allResources,
      });
    },
    [
      disabled,
      isAtUsageLimit,
      isAnalyzing,
      prompt,
      agentResponse,
      agentContext,
      ownsContext,
      permissionMode,
      pageContext,
      repoSelection.selectedNames,
      onSubmit,
      clearOnSubmit,
      conversationId,
    ],
  );

  // A non-empty selection is not the same as the right selection — the shared storage slot usually
  // arrives populated — so readiness waits for the preferred repos to actually land. When none of
  // them resolve to a selectable repo there is nothing left to wait for, and this reports ready.
  const isReady = !isLoadingVegaIntegrationStatus && (preferredRepoKeys.length === 0 || arePreferredReposApplied);

  useImperativeHandle(ref, () => ({ handleSendPrompt, isReady }), [handleSendPrompt, isReady]);

  const isPromptEmpty = !isAnalyzing && !prompt.trim() && !agentResponse.trim();
  const showUsageBanner =
    !isUsageBannerDismissed &&
    !!aiChatLimitData?.aiChatLimitStatus &&
    aiChatLimitData.aiChatLimitStatus.limit > 0 &&
    aiChatLimitData.aiChatLimitStatus.current_count / aiChatLimitData.aiChatLimitStatus.limit > 0.66;

  const getModeIcon = (): IconName => {
    if (permissionMode === 'read-write') {
      return 'graduation-cap';
    }
    return 'text-box-search';
  };

  const getPlaceholderText = () => {
    if (externalSourceHint) {
      const label = externalSourceLabel(externalSourceHint);
      return msg('vega.composer.askAboutSourcePlaceholder', { botName: AI_BOT_NAME, label });
    }
    return msg('vega.composer.placeholder', { botName: AI_BOT_NAME });
  };

  return (
    <>
      {enableSuggestedPrompts &&
        showSuggestedPromptsProp &&
        visiblePrompts.length > 0 &&
        !isAnalyzing &&
        !disabled &&
        !isAtUsageLimit && (
          <Box padding="8" paddingLeft="16" paddingRight="16" display="flex" flexDirection="column" gap="12">
            <Text size="medium" weight="bold">
              <Message id="vega.composer.suggestedPrompts" />
            </Text>
            <Box display="flex" flexDirection="row" gap="8" flexWrap="wrap" width="full">
              {visiblePrompts.map((sp) => (
                <div key={sp.key} className={exitingKey === sp.key ? styles.promptExit : styles.promptEnter}>
                  <Button
                    onPress={() => {
                      trackVegaAiSuggestedPromptClicked(sp.text);
                      handleSendPrompt(sp.text, true);
                    }}
                    variant="default"
                  >
                    <Icon name="brain" size="small" slot="start-icon" />
                    {sp.text}
                  </Button>
                </div>
              ))}
            </Box>
          </Box>
        )}

      <Box cssClass={styles.textAreaContainer}>
        {showIncompleteWarning && (
          <Alert status="warning" actionsLayout="inline">
            <AlertText>
              <LPText size="small">
                <Message id="vega.composer.conversationStopped" />
              </LPText>
            </AlertText>
          </Alert>
        )}

        {showUsageBanner && aiChatLimitData?.aiChatLimitStatus && (
          <UsageLimitBanner
            currentCount={aiChatLimitData.aiChatLimitStatus.current_count}
            limit={aiChatLimitData.aiChatLimitStatus.limit}
            onDismiss={() => setIsUsageBannerDismissed(true)}
          />
        )}

        <Box display="flex" alignItems="flex-end" gap="6" p="8" cssClass={styles.inputRow}>
          <Box flex="stretch" minWidth="none">
            <VegaMentionsInput
              ref={vegaMentionsRef}
              placeholder={getPlaceholderText()}
              disabled={composerChromeDisabled}
              initialContent={initialQuery || draftToTiptapHtml(initialDraft)}
              onTextChange={setPrompt}
              onSubmit={() => handleSendPrompt()}
            />
          </Box>
          <SendStopButton
            isAnalyzing={isAnalyzing}
            disabled={disabled}
            isPromptEmpty={isPromptEmpty}
            isAtUsageLimit={isAtUsageLimit}
            onStopGeneration={onStopGeneration}
            handleSendPrompt={handleSendPrompt}
          />
        </Box>

        {!simplifiedFooter && (
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Stack direction="row" gap="4" alignItems="center">
              <IconButton
                icon="gear-outline"
                variant="minimal"
                size="small"
                aria-label={msg('vega.composer.settingsLabel')}
                onPress={handleOpenSettings}
                isDisabled={composerChromeDisabled}
              />
              <PermissionModePicker
                permissionMode={permissionMode}
                githubIntegrated={Boolean(githubIntegrated)}
                disabled={composerChromeDisabled}
                iconLeft={getModeIcon()}
                onChange={(next) => {
                  if (next !== permissionMode) {
                    trackVegaAiModeChanged(next);
                    setPermissionMode(next);
                  }
                }}
              />
            </Stack>

            <Stack direction="row" gap="4" alignItems="center">
              {githubIntegrated && (
                <GitHubRepoSelector
                  {...repoSelection.selectorProps}
                  disabled={composerChromeDisabled}
                  triggerClassName={styles.secondaryText}
                  placement="top right"
                  githubIcon={<IconSolidGithub className={styles.secondaryIcon} size={14} />}
                  size="small"
                  headerContent={
                    <Box px="8" pt="8">
                      <Alert status="warning" actionsLayout="inline">
                        <AlertText>
                          <LPText size="small">
                            <Message
                              id="vega.composer.repoCloneNotice"
                              values={{
                                link: (chunks) => (
                                  <a
                                    href={getDocumentationUrl('home/observability/vega')}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                  >
                                    {' '}
                                    {chunks}{' '}
                                  </a>
                                ),
                              }}
                            />
                          </LPText>
                        </AlertText>
                      </Alert>
                    </Box>
                  }
                />
              )}
              {isLoadingVegaIntegrationStatus && (
                <Button size="small" variant="minimal" isDisabled className={styles.secondaryText}>
                  <IconSolidGithub className={styles.secondaryIcon} size={14} slot="start-icon" />{' '}
                  {msg('vega.composer.loadingRepositories')}
                </Button>
              )}
              <ConnectGitHubButton
                integration={githubIntegration}
                size="small"
                isDisabled={composerChromeDisabled}
                showExternalIcon={false}
                trackingComponent="VegaPromptComposer"
              />
            </Stack>
          </Box>
        )}
      </Box>
    </>
  );
};
